import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  boardCapacitySchema,
  listCardBulkBodySchema,
  listCardMoveAllBodySchema,
  listCardSortBodySchema,
  listCardsChangedSchema,
  type BoardCapacity,
  type BoardContents,
  type ListCardBulkBody,
  type ListCardMoveAllBody,
  type ListCardSortBody,
  type ListCardsChanged,
} from '@molt/shared';
import { announce } from './announce';
import { get, post } from './api';
import { useInterfaceLanguage } from './interface-language';
import { optimisticMutation } from './optimistic';
import { queryKeys } from './query-keys';

export function getBoardCapacity(boardId: string): Promise<BoardCapacity> {
  return get(`/boards/${encodeURIComponent(boardId)}/capacity`, boardCapacitySchema);
}

export function sortListCards(
  listId: string,
  body: ListCardSortBody,
): Promise<ListCardsChanged> {
  return post(
    `/lists/${encodeURIComponent(listId)}/cards/sort`,
    listCardsChangedSchema,
    listCardSortBodySchema.parse(body),
  );
}

export function archiveAllListCards(listId: string): Promise<ListCardsChanged> {
  return post(
    `/lists/${encodeURIComponent(listId)}/cards/archive-all`,
    listCardsChangedSchema,
    {},
  );
}

export function moveAllListCards(
  listId: string,
  body: ListCardMoveAllBody,
): Promise<ListCardsChanged> {
  return post(
    `/lists/${encodeURIComponent(listId)}/cards/move-all`,
    listCardsChangedSchema,
    listCardMoveAllBodySchema.parse(body),
  );
}

export function createBulkListCards(
  listId: string,
  body: ListCardBulkBody,
): Promise<ListCardsChanged> {
  return post(
    `/lists/${encodeURIComponent(listId)}/cards/bulk`,
    listCardsChangedSchema,
    listCardBulkBodySchema.parse(body),
  );
}

export function boardCapacityQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: queryKeys.boards.capacity(boardId),
    queryFn: () => getBoardCapacity(boardId),
  });
}

export interface SortListCardsVariables {
  readonly listId: string;
  readonly listTitle: string;
  readonly mode: ListCardSortBody['mode'];
}

export interface ArchiveAllListCardsVariables {
  readonly listId: string;
  readonly listTitle: string;
  readonly count: number;
}

export interface MoveAllListCardsVariables {
  readonly sourceListId: string;
  readonly sourceTitle: string;
  readonly destinationListId: string;
  readonly destinationTitle: string;
  readonly count: number;
}

export interface CreateBulkCardsVariables {
  readonly listId: string;
  readonly listTitle: string;
  readonly text: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
}

function invalidateCapacity(
  queryClient: ReturnType<typeof useQueryClient>,
  boardId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.capacity(boardId) });
}

function unchanged(previous: BoardContents | undefined): BoardContents | undefined {
  return previous;
}

export function applyListCardsSortResult(
  previous: BoardContents | undefined,
  variables: SortListCardsVariables,
  result: ListCardsChanged,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const sortedIds = result.cards.map((card) => card.id);
  const rank = new Map(sortedIds.map((id, index) => [id, index]));

  return {
    ...previous,
    lists: previous.lists.map((list) => {
      if (list.id !== variables.listId) return list;
      const sortedKnownCards = list.cards
        .filter((card) => rank.has(card.id))
        .sort((left, right) => rank.get(left.id)! - rank.get(right.id)!);
      const unseenCards = list.cards.filter((card) => !rank.has(card.id));
      return { ...list, cards: [...sortedKnownCards, ...unseenCards] };
    }),
  };
}

export function applyArchiveAllListCardsResult(
  previous: BoardContents | undefined,
  variables: ArchiveAllListCardsVariables,
  result: ListCardsChanged,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const archivedIds = new Set(result.cards.map((card) => card.id));
  return {
    ...previous,
    lists: previous.lists.map((list) =>
      list.id === variables.listId
        ? {
            ...list,
            cards: list.cards.filter((card) => !archivedIds.has(card.id)),
            openCardCount: Math.max(0, list.openCardCount - result.count),
          }
        : list,
    ),
  };
}

export function applyMoveAllListCardsResult(
  previous: BoardContents | undefined,
  variables: MoveAllListCardsVariables,
  result: ListCardsChanged,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const movedIds = result.cards.map((card) => card.id);
  const movedSet = new Set(movedIds);
  const movedCardsById = new Map<
    string,
    BoardContents['lists'][number]['cards'][number]
  >();

  for (const list of previous.lists) {
    if (list.id !== variables.sourceListId) continue;
    for (const card of list.cards) {
      if (movedSet.has(card.id)) movedCardsById.set(card.id, card);
    }
  }

  return {
    ...previous,
    lists: previous.lists.map((list) => {
      if (list.id === variables.sourceListId) {
        return {
          ...list,
          cards: list.cards.filter((card) => !movedSet.has(card.id)),
          cardCount: Math.max(0, list.cardCount - result.count),
          openCardCount: Math.max(0, list.openCardCount - result.count),
        };
      }

      if (list.id !== variables.destinationListId) return list;

      const movedCards = movedIds
        .map((id) => movedCardsById.get(id))
        .filter(
          (card): card is BoardContents['lists'][number]['cards'][number] =>
            card !== undefined,
        )
        .map((card) => ({ ...card, listId: variables.destinationListId }));

      return {
        ...list,
        cards: [...list.cards, ...movedCards],
        cardCount: list.cardCount + movedCards.length,
        openCardCount: list.openCardCount + movedCards.length,
      };
    }),
  };
}

export function useSortListCards(
  boardId: string,
): UseMutationResult<ListCardsChanged, unknown, SortListCardsVariables> {
  const { t } = useInterfaceLanguage();
  const queryClient = useQueryClient();
  const key = queryKeys.boards.contents(boardId);
  const handlers = optimisticMutation<SortListCardsVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: unchanged,
  });

  return useMutation({
    mutationFn: (variables) => sortListCards(variables.listId, { mode: variables.mode }),
    ...handlers,
    onSuccess: (result, variables) => {
      queryClient.setQueryData(key, (previous) =>
        applyListCardsSortResult(
          previous as BoardContents | undefined,
          variables,
          result,
        ),
      );
      announce(t('copyMove.listOps.announce.sorted', { list: variables.listTitle }));
      invalidateCapacity(queryClient, boardId);
    },
  });
}

export function useArchiveAllListCards(
  boardId: string,
): UseMutationResult<ListCardsChanged, unknown, ArchiveAllListCardsVariables> {
  const { t } = useInterfaceLanguage();
  const queryClient = useQueryClient();
  const key = queryKeys.boards.contents(boardId);
  const handlers = optimisticMutation<ArchiveAllListCardsVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: unchanged,
  });

  return useMutation({
    mutationFn: (variables) => archiveAllListCards(variables.listId),
    ...handlers,
    onSuccess: (result, variables) => {
      queryClient.setQueryData(key, (previous) =>
        applyArchiveAllListCardsResult(
          previous as BoardContents | undefined,
          variables,
          result,
        ),
      );
      announce(
        t(
          'copyMove.listOps.announce.archived',
          { count: variables.count, list: variables.listTitle },
          variables.count,
        ),
      );
      invalidateCapacity(queryClient, boardId);
    },
  });
}

export function useMoveAllListCards(
  boardId: string,
): UseMutationResult<ListCardsChanged, unknown, MoveAllListCardsVariables> {
  const { t } = useInterfaceLanguage();
  const queryClient = useQueryClient();
  const key = queryKeys.boards.contents(boardId);
  const handlers = optimisticMutation<MoveAllListCardsVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: unchanged,
  });

  return useMutation({
    mutationFn: (variables) =>
      moveAllListCards(variables.sourceListId, { listId: variables.destinationListId }),
    ...handlers,
    onSuccess: (result, variables) => {
      queryClient.setQueryData(key, (previous) =>
        applyMoveAllListCardsResult(
          previous as BoardContents | undefined,
          variables,
          result,
        ),
      );
      announce(
        t(
          'copyMove.listOps.announce.moved',
          { count: variables.count, list: variables.destinationTitle },
          variables.count,
        ),
      );
      invalidateCapacity(queryClient, boardId);
    },
  });
}

export function useCreateBulkCards(
  boardId: string,
): UseMutationResult<ListCardsChanged, unknown, CreateBulkCardsVariables> {
  const { t } = useInterfaceLanguage();
  const queryClient = useQueryClient();
  const key = queryKeys.boards.contents(boardId);
  const handlers = optimisticMutation<CreateBulkCardsVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: unchanged,
  });

  return useMutation({
    mutationFn: (variables) =>
      createBulkListCards(variables.listId, {
        text: variables.text,
        previousCardId: variables.previousCardId,
        nextCardId: variables.nextCardId,
      }),
    ...handlers,
    onSuccess: (result, variables) => {
      announce(
        t(
          'copyMove.listOps.announce.added',
          { count: result.count, list: variables.listTitle },
          result.count,
        ),
      );
      invalidateCapacity(queryClient, boardId);
    },
  });
}
